import { SelectProps } from 'antd/lib/select';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import WarningModal from 'components/modals/WarningModal';
import { InternalAdminRole } from 'constants/internal-admin-roles';
import useBackOfficeUser from 'store/modules/backOfficeUser/hooks';
import { removeInternalAdminRole } from 'store/modules/backOfficeUsers/actions';
import ActionTypes from 'store/modules/backOfficeUsers/constants';
import { useIsLoading } from 'store/modules/loading/hooks';
import { checkIsInternalAdmin } from 'utils/AccessControl/check-is-internal-admin';

import { InternalAdminRoleSelect } from './InternalAdminRoleSelect';

type Props = SelectProps<InternalAdminRole> & {
  onDismiss?: () => void;
};

/**
 * Enhanced version of InternalAdminRoleSelect that shows a confirmation modal
 * when changing from internal admin to normal user.
 *
 * NOTE: This component relies on `useBackOfficeUser` as the source-of-truth for the current user,
 * as it assumes its usage is within `BackOfficeUserFormItems`.
 */
export function InternalAdminRoleSelectWithConfirmation({ onDismiss, ...props }: Props) {
  const [mustConfirm, setMustConfirm] = useState(false);
  const backOfficeUser = useBackOfficeUser();
  const dispatch = useDispatch();
  const isRemovingInternalAdminRole = useIsLoading(ActionTypes.REMOVE_INTERNAL_ADMIN_ROLE_REQUEST);

  const isInternalAdmin = backOfficeUser && checkIsInternalAdmin(backOfficeUser);
  useEffect(() => {
    if (!isInternalAdmin) {
      // Close modal after successful removal
      setMustConfirm(false);
    }
  }, [isInternalAdmin]);

  const removeRole = () => {
    if (backOfficeUser) {
      dispatch(removeInternalAdminRole(backOfficeUser.id));
    }
  };

  const handleChange: SelectProps<InternalAdminRole>['onChange'] = (value, option) => {
    const originalValue = backOfficeUser?.adminRole;

    if (!originalValue) {
      // No original value yet, just proceed
      props.onChange?.(value, option);
      return;
    }

    const wasInternalAdmin = checkIsInternalAdmin({ adminRole: originalValue });
    const isNowInternalAdmin = checkIsInternalAdmin({ adminRole: value });

    // Show confirmation when changing FROM internal admin TO non-admin
    if (wasInternalAdmin && !isNowInternalAdmin) {
      setMustConfirm(true);
    } else {
      // For all other changes, proceed normally
      props.onChange?.(value, option);
    }
  };

  return (
    <>
      <InternalAdminRoleSelect {...props} onChange={handleChange} />
      <WarningModal
        confirmText="Yes, change role"
        isConfirmButtonLoading={isRemovingInternalAdminRole}
        isOpen={mustConfirm}
        onConfirm={removeRole}
        onDismiss={onDismiss}
        title="Remove Internal Admin Role"
        width={450}
      >
        <strong>This change cannot be undone.</strong>
        {' '}Do you want to convert this internal user into a normal user?
        Doing so will allow you to manage their memberships and invitations.
      </WarningModal>
    </>
  );
}
